import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, MapPin, Smartphone, Shield, BarChart, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const TimesheetFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Clock,
      title: 'GPS Time Clock',
      description: 'Employees clock in/out from their mobile devices with GPS verification to ensure they\'re at the correct work location.'
    },
    {
      icon: MapPin,
      title: 'Geofencing Technology',
      description: 'Set virtual boundaries around work sites. Automatic clock-in when employees enter the geofenced area.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Time Tracking',
      description: 'iOS and Android apps allow employees to clock in/out, request time off, and view their hours from anywhere.'
    },
    {
      icon: Shield,
      title: 'Buddy Punch Prevention',
      description: 'Biometric verification and photo capture prevents time theft and ensures accurate time tracking.'
    },
    {
      icon: BarChart,
      title: 'Real-Time Reporting',
      description: 'Live dashboards show who\'s working, Employee costs, overtime alerts, and attendance patterns instantly.'
    },
    {
      icon: CheckCircle2,
      title: 'Automatic Breaks',
      description: 'System automatically deducts breaks and ensures compliance with Employee laws and meal period regulations.'
    }
  ];

  const benefits = [
    'Eliminate time theft and buddy punching completely',
    '99.9% accurate time tracking with GPS and biometrics',
    'Reduce payroll processing time by 75%',
    'Automatic overtime calculations and alerts',
    'Seamless integration with payroll systems',
    'Real-time Employee cost tracking and budgeting',
    'Digital approval workflows for timesheets',
    'Automatic rounding rules configuration',
    'Comprehensive audit trail for compliance',
    'Mobile access for remote and field workers'
  ];

  const useCases = [
    {
      title: 'Field Service Teams',
      description: 'Track technicians, contractors, and mobile workers across multiple job sites with GPS verification. Know exactly where your team is and what they\'re working on in real-time.'
    },
    {
      title: 'Healthcare Facilities',
      description: 'Ensure accurate shift tracking for nurses and medical staff. Verify on-site presence for patient safety and regulatory compliance with biometric authentication.'
    },
    {
      title: 'Construction Sites',
      description: 'Monitor worker hours across multiple construction sites with geofencing. Prevent time theft and ensure workers are on the correct job site with GPS tracking.'
    },
    {
      title: 'Retail Operations',
      description: 'Track employee hours across multiple store locations. Prevent early clock-ins and late clock-outs with scheduled shift enforcement and photo verification.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-green-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Advanced Timesheet & Attendance
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8 max-w-4xl mx-auto">
              GPS-verified time tracking that eliminates time theft, ensures accuracy, and integrates seamlessly with payroll. Save thousands on Employee costs annually.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-green-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-green-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Timesheet Page.png" 
              alt="Advanced Timesheet & Attendance Interface"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">99.9%</div>
              <div className="text-gray-600">Time Accuracy</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">75%</div>
              <div className="text-gray-600">Payroll Time Saved</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">$8K+</div>
              <div className="text-gray-600">Annual Savings</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Comprehensive Time Tracking Features
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Everything you need for accurate, compliant, and fraud-proof employee time tracking.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Time Tracking?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Eliminate payroll errors, prevent time theft, and ensure Employee law compliance with the most accurate time tracking system available.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Employee Clocks In</h4>
                    <p className="text-gray-600">Open mobile app or use web portal. System captures GPS location and optional photo.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Location Verified</h4>
                    <p className="text-gray-600">GPS coordinates checked against geofenced work sites. Unauthorized locations flagged.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Time Tracked</h4>
                    <p className="text-gray-600">Hours logged automatically. Breaks deducted per company policy and Employee laws.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Payroll Integration</h4>
                    <p className="text-gray-600">Approved hours exported directly to your payroll system. Zero manual data entry.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Trusted Across Industries
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            From field workers to office staff, our time tracking works everywhere.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Stop Losing Money to Time Theft
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Join thousands of businesses saving $8,000+ annually with accurate time tracking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default TimesheetFeature;
